import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import { mapAppSubscriptions } from '../../../organisations/helpers/appSubscriptions/mapAppSubscriptions';
import createOrgAppSubscriptionService from '../../services/createOrgAppSubscriptionService';
import updateOrgAppSubscriptionService from '../../services/updateOrgAppSubscriptionService';
import orgAppSubscriptionsLookupService from '../../services/orgAppSubscriptionsLookupService';
import roleAssignmentCommunityMembersService from '../../services/roleAssignmentCommunityMembersService';
import communityAppsService from '../../services/communityAppsService';
import featureSubscriptionCommunitiesService from '../../services/featureSubscriptionCommunitiesService';

const FORM_NAME = 'addOrgAppSubscription';
const REQUIRED_FIELDS = ['communityId', 'organisationId'];
const selector = formValueSelector(FORM_NAME);

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);
  return errors;
};

const AddSubscriptionModal = ({
  appId,
  installationId,
  featureId,
  featureTag,
  onClose,
  onCreated,
  dispatchCreateOrgAppSubscriptionPost,
  createOrgAppSubscriptionFetch,
  dispatchUpdateOrgAppSubscriptionPut,
  updateOrgAppSubscriptionFetch,
  dispatchOrgAppSubscriptionsLookupGet,
  orgAppSubscriptionsLookupFetch,
  dispatchCommunityAppsLookupGet,
  communityAppsLookupFetch,
  dispatchFeatureSubscriptionCommunitiesGet,
  featureSubscriptionCommunitiesFetch,
  dispatchRoleAssignmentCommunityMembersGet,
  roleAssignmentCommunityMembersFetch,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const communityId = useSelector(state => selector(state, 'communityId'));
  const organisationId = useSelector(state => selector(state, 'organisationId'));

  useEffect(() => {
    dispatchCommunityAppsLookupGet(installationId);
  }, [dispatchCommunityAppsLookupGet, installationId]);

  useEffect(() => {
    dispatchFeatureSubscriptionCommunitiesGet(appId, installationId, featureId);
  }, [dispatchFeatureSubscriptionCommunitiesGet, appId, installationId, featureId]);

  const communityApps = useMemo(
    () =>
      communityAppsLookupFetch.value?.items || communityAppsLookupFetch.value || [],
    [communityAppsLookupFetch.value]
  );

  // The combo box only shows communities actually subscribed to this feature;
  // communityApps (installation-scoped) is kept only to resolve the
  // communityApp id needed when posting a new subscription.
  const communities = useMemo(
    () =>
      (featureSubscriptionCommunitiesFetch.value || []).map(c => ({
        key: c.id,
        value: c.id,
        text: c.name,
      })),
    [featureSubscriptionCommunitiesFetch.value]
  );

  const organisations = useMemo(() => {
    const members =
      roleAssignmentCommunityMembersFetch.value?.items ||
      roleAssignmentCommunityMembersFetch.value ||
      [];
    return members
      .filter(m => m.organisation)
      .map(m => ({
        key: m.organisation.id,
        value: m.organisation.id,
        text: m.organisation.name,
      }));
  }, [roleAssignmentCommunityMembersFetch.value]);

  const handleCommunityChange = useCallback(
    (_, newValue) => {
      dispatch(change(FORM_NAME, 'organisationId', null));
      if (newValue) dispatchRoleAssignmentCommunityMembersGet(newValue);
    },
    [dispatch, dispatchRoleAssignmentCommunityMembersGet]
  );

  const handleOrganisationChange = useCallback(
    (_, newValue) => {
      if (newValue) dispatchOrgAppSubscriptionsLookupGet(newValue, installationId);
    },
    [dispatchOrgAppSubscriptionsLookupGet, installationId]
  );

  const handleSubmit = values => {
    const body = { appInstallationFeature: { tag: featureTag } };
    const orgApps = orgAppSubscriptionsLookupFetch.value || [];
    const existingOrgApp = orgApps.find(oa => oa.community?.id === values.communityId);

    if (existingOrgApp) {
      // The organisation already has this community app: PUT the merged feature
      // list so its other feature subscriptions are preserved (same pattern as
      // AppSubscriptions.handleSubmit).
      dispatchUpdateOrgAppSubscriptionPut(
        mapAppSubscriptions(values.organisationId, existingOrgApp.id, existingOrgApp, body),
        onCreated,
        undefined,
        'addSubscriptionSuccess',
        'addSubscriptionFailed',
        t
      );
    } else {
      const communityApp = communityApps.find(ca => ca.community?.id === values.communityId);

      dispatchCreateOrgAppSubscriptionPost(
        mapAppSubscriptions(values.organisationId, communityApp?.id, undefined, body),
        onCreated,
        undefined,
        t
      );
    }
  };

  const submitProcessing =
    createOrgAppSubscriptionFetch.pending || updateOrgAppSubscriptionFetch.pending;

  return (
    <FormModal
      closeIcon
      header={t('addSubscription')}
      dataLoading={false}
      submitProcessing={submitProcessing}
      onSubmit={handleSubmit}
      onCancel={onClose}
      data={{}}
      t={t}
      validate={validate}
      form={FORM_NAME}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="plus"
      saveButtonContent={t('addSubscription')}
      saveDisabled={!communityId || !organisationId || orgAppSubscriptionsLookupFetch.pending}
    >
      <Grid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="communityId"
            valueComponent={SelectFieldValueComponent}
            label={t('community')}
            options={communities}
            loading={featureSubscriptionCommunitiesFetch.pending}
            disabled={submitProcessing || featureSubscriptionCommunitiesFetch.pending}
            onChange={handleCommunityChange}
            search
            fluid
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="organisationId"
            valueComponent={SelectFieldValueComponent}
            label={t('organisation')}
            options={organisations}
            loading={roleAssignmentCommunityMembersFetch.pending}
            disabled={
              !communityId || submitProcessing || roleAssignmentCommunityMembersFetch.pending
            }
            onChange={handleOrganisationChange}
            search
            fluid
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

AddSubscriptionModal.propTypes = {
  appId: PropTypes.string.isRequired,
  installationId: PropTypes.string.isRequired,
  featureId: PropTypes.string.isRequired,
  featureTag: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  dispatchCreateOrgAppSubscriptionPost: PropTypes.func.isRequired,
  createOrgAppSubscriptionFetch: PropTypes.object.isRequired,
  dispatchUpdateOrgAppSubscriptionPut: PropTypes.func.isRequired,
  updateOrgAppSubscriptionFetch: PropTypes.object.isRequired,
  dispatchOrgAppSubscriptionsLookupGet: PropTypes.func.isRequired,
  orgAppSubscriptionsLookupFetch: PropTypes.object.isRequired,
  dispatchCommunityAppsLookupGet: PropTypes.func.isRequired,
  communityAppsLookupFetch: PropTypes.object.isRequired,
  dispatchFeatureSubscriptionCommunitiesGet: PropTypes.func.isRequired,
  featureSubscriptionCommunitiesFetch: PropTypes.object.isRequired,
  dispatchRoleAssignmentCommunityMembersGet: PropTypes.func.isRequired,
  roleAssignmentCommunityMembersFetch: PropTypes.object.isRequired,
};

export default compose(
  createOrgAppSubscriptionService,
  updateOrgAppSubscriptionService,
  orgAppSubscriptionsLookupService,
  roleAssignmentCommunityMembersService,
  communityAppsService,
  featureSubscriptionCommunitiesService
)(AddSubscriptionModal);
